import React from 'react'
import Subscription, { ISubscription } from '@aytm/lib/common/subscription'
import DevLog from '../devlog/Module'
import { ModuleID } from '../types'
import { IThemeEditorToolbar, IThemeEditorToolbarProps, IThemeEditorModule, ICustomTheme } from './types'

let ThemeEditorToolbar: React.MemoExoticComponent<
  React.ForwardRefExoticComponent<IThemeEditorToolbarProps & React.RefAttributes<IThemeEditorToolbar>>
> = null

const themeEditorToolbarProps: IThemeEditorToolbarProps = {
  show: true,
  collapsible: true,
  accentColors: [],
  predefinedThemes: {},
  imageAssetCreateUrl: null,
  imageAssetDeleteUrl: null,
  authenticityToken: null,
  saveThemeURL: null,
  currentTheme: {
    bgID: null,
    tp: null,
    acHex: null,
    bgHex: null,
    tagline: null,
    pb: null,
    logoAssetID: null,
    logoURL: null
  },
  defaultTheme: {
    bgID: null,
    acHex: '',
    bgHex: '',
    previewImgURL: ''
  }
}

let updateSubscription: ISubscription<IThemeEditorModule> = null

const model: ICustomTheme = {
  acHex: '',
  logoURL: '',
  tagline: '',
  logoAssetID: 0,
  bgAssetID: 0,
  bgID: 0,
  bgHex: '',
  tile: false,
  fit: true,
  tp: true,
  pb: true
}

let moduleLoaded = false
const ThemeEditorModule: IThemeEditorModule = {
  get id(): ModuleID {
    return ModuleID.THEME_EDITOR
  },
  config({
    toolbar,
    accentColors,
    currentTheme,
    defaultTheme,
    predefinedThemes,
    saveThemeURL,
    imageAssetCreateUrl,
    imageAssetDeleteUrl,
    authenticityToken
  } = {}) {
    if (null != toolbar?.show) {
      themeEditorToolbarProps.show = toolbar.show
    }
    if (null != toolbar?.collapsible) {
      themeEditorToolbarProps.collapsible = toolbar.collapsible
    }
    if (accentColors.length) {
      themeEditorToolbarProps.accentColors = accentColors
    }
    if (predefinedThemes) {
      themeEditorToolbarProps.predefinedThemes = predefinedThemes
    }
    if (saveThemeURL) {
      themeEditorToolbarProps.saveThemeURL = saveThemeURL
    }
    if (currentTheme) {
      model.bgID = currentTheme.bgID
      model.bgAssetID = currentTheme.bgAssetID
      model.pb = currentTheme.pb
      model.tp = currentTheme.tp
      model.acHex = currentTheme.acHex
      model.bgHex = currentTheme.bgHex
      model.tagline = currentTheme.tagline
      model.logoAssetID = currentTheme.logoAssetID
      model.logoURL = currentTheme.logoURL
      model.tagline = currentTheme.tagline
    }
    if (imageAssetCreateUrl) {
      themeEditorToolbarProps.imageAssetCreateUrl = imageAssetCreateUrl
    }
    if (imageAssetDeleteUrl) {
      themeEditorToolbarProps.imageAssetDeleteUrl = imageAssetDeleteUrl
    }
    if (authenticityToken) {
      themeEditorToolbarProps.authenticityToken = authenticityToken
    }
    themeEditorToolbarProps.currentTheme = currentTheme
    themeEditorToolbarProps.defaultTheme = defaultTheme

    return ThemeEditorModule
  },
  async load() {
    try {
      ;({ ThemeEditorToolbar } = (await import('./components/index')).default)
      DevLog._log(ThemeEditorModule, 'load ThemeEditorModule')
      moduleLoaded = true
    } catch (error) {
      DevLog.moduleError(ThemeEditorModule, error, 'load ThemeEditorModule')
    }
    return ThemeEditorModule
  },
  get loaded() {
    return moduleLoaded
  },
  get show() {
    return themeEditorToolbarProps.show
  },
  get collapsible() {
    return themeEditorToolbarProps.collapsible
  },
  get accentColors() {
    return themeEditorToolbarProps.accentColors
  },
  get predefinedThemes() {
    return themeEditorToolbarProps.predefinedThemes
  },
  get authenticityToken() {
    return themeEditorToolbarProps.authenticityToken
  },
  get imageAssetCreateUrl() {
    return themeEditorToolbarProps.imageAssetCreateUrl
  },
  get imageAssetDeleteUrl() {
    return themeEditorToolbarProps.imageAssetDeleteUrl
  },
  get currentTheme() {
    return themeEditorToolbarProps.currentTheme
  },
  get defaultTheme() {
    return themeEditorToolbarProps.defaultTheme
  },
  get saveThemeURL() {
    return themeEditorToolbarProps.saveThemeURL
  },
  get ThemeEditorToolbar() {
    return ThemeEditorToolbar
  },
  get tagline() {
    return model.tagline
  },
  set tagline(value) {
    model.tagline = value
    if (null != updateSubscription) {
      updateSubscription.update(ThemeEditorModule)
    }
  },
  get pb() {
    return model.pb
  },
  set pb(value) {
    model.pb = value
    if (null != updateSubscription) {
      updateSubscription.update(ThemeEditorModule)
    }
  },
  get tp() {
    return model.tp
  },
  set tp(value) {
    model.tp = value
    if (null != updateSubscription) {
      updateSubscription.update(ThemeEditorModule)
    }
  },
  get model() {
    return model
  },
  get acHex() {
    return model.acHex
  },
  set acHex(value) {
    model.acHex = value
    if (null != updateSubscription) {
      updateSubscription.update(ThemeEditorModule)
    }
  },
  get bgHex() {
    return model.bgHex
  },
  set bgHex(value) {
    model.bgHex = value
    if (null != updateSubscription) {
      updateSubscription.update(ThemeEditorModule)
    }
  },
  get logoURL() {
    return model.logoURL
  },
  set logoURL(value) {
    model.logoURL = value
    if (null != updateSubscription) {
      updateSubscription.update(ThemeEditorModule)
    }
  },
  get logoAssetID() {
    return model.logoAssetID
  },
  set logoAssetID(value) {
    model.logoAssetID = value
    if (null != updateSubscription) {
      updateSubscription.update(ThemeEditorModule)
    }
  },
  get bgID() {
    return model.bgID
  },
  set bgID(value) {
    model.bgID = value
    if (null != updateSubscription) {
      updateSubscription.update(ThemeEditorModule)
    }
  },
  get bgAssetID() {
    return model.bgAssetID
  },
  set bgAssetID(value) {
    model.bgAssetID = value
    if (null != updateSubscription) {
      updateSubscription.update(ThemeEditorModule)
    }
  },
  onUpdate(listener) {
    if (null == updateSubscription) {
      updateSubscription = Subscription<IThemeEditorModule>()
    }
    return updateSubscription.listen(listener)
  }
}

export default ThemeEditorModule
